var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useState, useEffect } from "react";
import isEqual from "lodash/isEqual";
import { useHistory, useLocation } from "react-router-dom";
import { allOf, anyOf, contains, equals } from "../utils/cqlUtils";
import { DEFAULT_PERSON_FILTER_STATUSES, sanitizePersonFilterStatuses, } from "../utils/personFilterStatuses";
import { toggleRequiredSelection } from "../utils/requiredSelection";
import { AUTHORITY_LINKED_PARAM, buildAuthorityLinkedClause, buildPersonIdentifierClause, sanitizeAuthorityLinked, } from "../utils/personAuthorityLink";
import { buildProposalOriginClause, PROPOSAL_ORIGIN_PARAM, sanitizeProposalOrigin, } from "../utils/personProposalOrigin";
// True when the two status selections hold the same set of values (order-independent).
var sameStatusSet = function (a, b) {
    return a.length === b.length && __spreadArray([], a, true).sort().join() === __spreadArray([], b, true).sort().join();
};
// Name filters match the primary name and the alternative names, the same way the legal person
// index does - a person is findable by every name they are registered under. The two name
// clauses are evaluated independently by the backend, so a match may come from the primary name
// and an alternative name at the same time.
// The proposer clause keys on the sourceId already stored in `proposerId`, so no resolved
// publisher is needed to build the query.
export var buildNaturalPersonQuery = function (filterValues) {
    var queryParts = [];
    var lastNameTerm = filterValues.lastName.trim();
    var firstNameTerm = filterValues.firstName.trim();
    var proposerTerm = filterValues.proposerId.trim();
    if (lastNameTerm) {
        queryParts.push(anyOf(contains("lastName", lastNameTerm), contains("naturalAlternativeNames.lastName", lastNameTerm)));
    }
    if (firstNameTerm) {
        queryParts.push(anyOf(contains("firstName", firstNameTerm), contains("naturalAlternativeNames.firstName", firstNameTerm)));
    }
    var identifierClause = buildPersonIdentifierClause(filterValues.identifier);
    if (identifierClause) {
        queryParts.push(identifierClause);
    }
    if (filterValues.statuses.length > 0) {
        queryParts.push(anyOf.apply(void 0, filterValues.statuses.map(function (status) { return equals("status", status); })));
    }
    if (proposerTerm) {
        // Filter by the proposer's sourceId (a varchar to-one join). The previous proposer.id clause
        // compared the publisher UUID and required an extra publisher lookup to resolve; the sourceId
        // is exactly what the dropdown and "moje návrhy" already put into the filter.
        queryParts.push(equals("proposer.sourceId", proposerTerm));
    }
    var authorityLinkedClause = buildAuthorityLinkedClause(filterValues.authorityLinked);
    if (authorityLinkedClause) {
        queryParts.push(authorityLinkedClause);
    }
    var proposalOriginClause = buildProposalOriginClause(filterValues.proposalOrigin);
    if (proposalOriginClause) {
        queryParts.push(proposalOriginClause);
    }
    return allOf(queryParts);
};
/**
 * The applied filters as the URL holds them.
 *
 * @param search `location.search` of the list
 * @param canSeeSearchFailed whether the SEARCH_FAILED status may be kept
 * @param canFilterProposalOrigin whether the "Původ návrhu" filter may be kept
 */
var filtersFromSearch = function (search, canSeeSearchFailed, canFilterProposalOrigin) {
    var queryParams = new URLSearchParams(search);
    return {
        lastName: queryParams.get("lastName") || "",
        firstName: queryParams.get("firstName") || "",
        identifier: queryParams.get("identifier") || "",
        // When the URL carries no status params at all fall back to the default view rather than
        // "all statuses" (which would surface SEARCH_FAILED records unexpectedly).
        statuses: sanitizePersonFilterStatuses(queryParams.has("statuses")
            ? queryParams.getAll("statuses")
            : DEFAULT_PERSON_FILTER_STATUSES, canSeeSearchFailed),
        proposerId: queryParams.get("proposerId") || "",
        authorityLinked: sanitizeAuthorityLinked(queryParams.get(AUTHORITY_LINKED_PARAM)),
        proposalOrigin: canFilterProposalOrigin
            ? sanitizeProposalOrigin(queryParams.get(PROPOSAL_ORIGIN_PARAM))
            : "",
    };
};
// `canSeeSearchFailed` (agency worker / superuser) allows the SEARCH_FAILED status; for other users
// it is dropped from the URL selection so a shared link cannot surface those records.
// `rolesKnown` is false while the user's roles are still loading: a URL asking for SEARCH_FAILED
// is then not searched yet, it would be sent without that status and then again with it.
// `canFilterProposalOrigin` (agency worker / superuser) allows the "Původ návrhu" filter; for other
// users a proposalOrigin URL param is dropped, as SEARCH_FAILED is (DB-899). It waits for the roles
// the same way.
export var useNaturalPersonFilters = function (canSeeSearchFailed, rolesKnown, canFilterProposalOrigin) {
    if (canSeeSearchFailed === void 0) { canSeeSearchFailed = false; }
    if (rolesKnown === void 0) { rolesKnown = true; }
    if (canFilterProposalOrigin === void 0) { canFilterProposalOrigin = true; }
    var history = useHistory();
    var location = useLocation();
    var initialFilters = {
        lastName: "",
        firstName: "",
        identifier: "",
        statuses: DEFAULT_PERSON_FILTER_STATUSES,
        proposerId: "",
        authorityLinked: "",
        proposalOrigin: "",
    };
    // Initialised from the URL in the first render: filters patched in by an
    // effect afterwards would change the search and send it twice.
    var _a = useState(function () {
        return filtersFromSearch(location.search, canSeeSearchFailed, canFilterProposalOrigin);
    }), filters = _a[0], setFilters = _a[1];
    var _b = useState(filters), appliedFilters = _b[0], setAppliedFilters = _b[1];
    var _c = useState(""), publisherQuery = _c[0], setPublisherQuery = _c[1];
    var updateUrlWithFilters = function (newFilters) {
        var params = new URLSearchParams();
        if (newFilters.lastName)
            params.set("lastName", newFilters.lastName);
        if (newFilters.firstName)
            params.set("firstName", newFilters.firstName);
        if (newFilters.identifier)
            params.set("identifier", newFilters.identifier);
        if (newFilters.proposerId)
            params.set("proposerId", newFilters.proposerId);
        newFilters.statuses.forEach(function (status) { return params.append("statuses", status); });
        if (newFilters.authorityLinked)
            params.set(AUTHORITY_LINKED_PARAM, newFilters.authorityLinked);
        if (newFilters.proposalOrigin)
            params.set(PROPOSAL_ORIGIN_PARAM, newFilters.proposalOrigin);
        history.push({ search: params.toString() });
    };
    var handleFilterChange = function (field, value) {
        var _a;
        var newFilters = __assign(__assign({}, filters), (_a = {}, _a[field] = value, _a));
        setFilters(newFilters);
    };
    var handleStatusChange = function (status) {
        var newStatuses = toggleRequiredSelection(filters.statuses, status);
        if (newStatuses === filters.statuses)
            return;
        handleFilterChange("statuses", newStatuses);
    };
    var handlePublisherInput = function (inputValue) {
        if (inputValue !== publisherQuery) {
            setPublisherQuery(inputValue);
        }
    };
    var handleApplyFilters = function () {
        setAppliedFilters(filters);
        updateUrlWithFilters(filters);
    };
    // Quick "moje návrhy" action: filter to the logged-in publisher's own proposals and apply at once.
    var applyMyProposals = function (publisherSourceId) {
        var newFilters = __assign(__assign({}, filters), { proposerId: publisherSourceId });
        setFilters(newFilters);
        setAppliedFilters(newFilters);
        updateUrlWithFilters(newFilters);
    };
    var handleClearFilters = function () {
        var clearedFilters = initialFilters;
        setFilters(clearedFilters);
        setAppliedFilters(clearedFilters);
        setPublisherQuery("");
        updateUrlWithFilters(clearedFilters);
    };
    var isAnyFilterActive = function () {
        return (appliedFilters.lastName.length > 0 ||
            appliedFilters.firstName.length > 0 ||
            appliedFilters.identifier.length > 0 ||
            !sameStatusSet(appliedFilters.statuses, DEFAULT_PERSON_FILTER_STATUSES) ||
            appliedFilters.proposerId.length > 0 ||
            appliedFilters.authorityLinked.length > 0 ||
            appliedFilters.proposalOrigin.length > 0);
    };
    var hasUnappliedChanges = function () {
        return JSON.stringify(filters) !== JSON.stringify(appliedFilters);
    };
    var buildQuery = function (filterValues) {
        if (filterValues === void 0) { filterValues = appliedFilters; }
        return buildNaturalPersonQuery(filterValues);
    };
    useEffect(function () {
        var newFilters = filtersFromSearch(location.search, canSeeSearchFailed, canFilterProposalOrigin);
        if (!isEqual(newFilters, appliedFilters)) {
            setFilters(newFilters);
            setAppliedFilters(newFilters);
        }
    }, [
        location.search,
        appliedFilters,
        canSeeSearchFailed,
        canFilterProposalOrigin,
    ]);
    // Not ready while the roles that decide SEARCH_FAILED or the proposal origin
    // filter are unknown and the URL asks for it, nor for the one render in which
    // the applied filters still lag behind roles that have just loaded.
    var filtersReady = (rolesKnown ||
        isEqual(appliedFilters, filtersFromSearch(location.search, true, true))) &&
        isEqual(appliedFilters, filtersFromSearch(location.search, canSeeSearchFailed, canFilterProposalOrigin));
    return {
        filters: filters,
        appliedFilters: appliedFilters,
        /** false while the URL filters still depend on roles that are not known yet */
        filtersReady: filtersReady,
        publisherQuery: publisherQuery,
        handleFilterChange: handleFilterChange,
        handleStatusChange: handleStatusChange,
        handlePublisherInput: handlePublisherInput,
        handleApplyFilters: handleApplyFilters,
        applyMyProposals: applyMyProposals,
        handleClearFilters: handleClearFilters,
        isAnyFilterActive: isAnyFilterActive,
        hasUnappliedChanges: hasUnappliedChanges,
        buildQuery: buildQuery,
    };
};
